/**
 * CR339: Configure Data Elements Core
 * 
 * Editor for data-element-owned fields:
 * - Structure toggles + ordering (primary & details surfaces)
 * - Details content configuration
 * - Promo badges enable/order configuration
 * 
 * Does NOT edit component-owned fields (ribbon, header composition, labels, styling).
 * Preview uses saved component values (not component drafts).
 */

import { useMemo, useState, useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  LayoutGrid,
  FileText,
  Award,
  Save,
  Undo2,
  AlertCircle,
} from "lucide-react";
import { PublishTargetIndicator } from "@/components/admin/deal-cards/EnvironmentSelector";
import type { PublishEnvironment } from "@/hooks/usePromoteDealCardConfig";

import {
  SectionChecklist,
  PreviewWorkbench,
  type SurfaceBlockToggles,
} from "@/components/admin/deal-cards";
import { PromoBadgesAccordion, type PromoBadgesConfig } from "@/components/admin/deal-cards/PromoBadgesAccordion";
import { DetailsContentAccordion, type DetailsContentConfig } from "@/components/admin/deal-cards/DetailsContentAccordion";
import { useDealSourceMode } from "@/hooks/useDealSourceMode";
import { DealSourcePicker } from "@/components/admin/deal-cards/DealSourcePicker";
import { DEFAULT_FACES_CONFIG, type CardFacesConfig } from "@/lib/deals/cardFaces";
import type { BlockSurface } from "@/lib/deal-cards/specV2";
import type { ExtendedSectionToggles } from "./SectionTogglesPanel";
import type { CardStyling } from "./CardStylingPanel";
import type { RibbonDraft } from "./RibbonSettingsPanel";
import type { HeaderComposition } from "./HeaderCompositionPanel";
import type { DealTypeRegistryEntry, DealCardTemplate, CardTemplateLabels } from "@/providers/DealCardConfigProvider";
import type { ComponentsConfig } from "./DealCardsTab";
import { safeLower } from "@/lib/safeString";

// ================================================================
// TYPES
// ================================================================

export interface ConfigureDataElementsCoreProps {
  // Registry & Templates
  registry: DealTypeRegistryEntry[];
  templates: DealCardTemplate[];
  selectedType: string;
  onTypeChange: (type: string) => void;
  
  // Data Elements draft state
  draftSections: ExtendedSectionToggles;
  draftSurfaceBlockToggles: SurfaceBlockToggles;
  draftSurfaceBlockOrder: { primary: string[]; details: string[] };
  draftSectionOrder: string[];
  draftPromoBadges: PromoBadgesConfig;
  draftDetailsContent: DetailsContentConfig;
  
  // Handlers
  onSectionToggle: (key: keyof ExtendedSectionToggles, value: boolean) => void;
  onSurfaceBlockToggle: (surface: BlockSurface, blockId: string, enabled: boolean) => void;
  onSurfaceBlockOrderChange: (surface: BlockSurface, newOrder: string[]) => void;
  onSectionOrderChange: (order: string[]) => void;
  onStylingChange: (update: Partial<CardStyling>) => void;
  onPromoBadgesChange: (config: PromoBadgesConfig) => void;
  onDetailsContentChange: (config: DetailsContentConfig) => void;
  
  // Saved components (for preview rendering — read-only here)
  savedRibbon: RibbonDraft;
  savedHeader: HeaderComposition;
  savedLabels: CardTemplateLabels;
  savedStyling: CardStyling;
  savedThemePreset: "light" | "dark" | "custom";
  savedComponents: ComponentsConfig;
  
  // Actions
  onSave: () => void;
  onDiscard: () => void;
  
  // State
  hasUnsavedChanges: boolean;
  saving: boolean;
  loading: boolean;
  /** CR413: Which environment this editor publishes to */
  publishEnvironment?: PublishEnvironment;
}

// ================================================================
// COMPONENT
// ================================================================

export function ConfigureDataElementsCore({
  registry,
  templates,
  selectedType,
  onTypeChange,
  draftSections,
  draftSurfaceBlockToggles,
  draftSurfaceBlockOrder,
  draftSectionOrder,
  draftPromoBadges,
  draftDetailsContent,
  onSectionToggle,
  onSurfaceBlockToggle,
  onSurfaceBlockOrderChange,
  onSectionOrderChange,
  onStylingChange,
  onPromoBadgesChange,
  onDetailsContentChange,
  savedRibbon,
  savedHeader,
  savedLabels,
  savedStyling,
  savedThemePreset,
  savedComponents,
  onSave,
  onDiscard,
  hasUnsavedChanges,
  saving,
  loading,
  publishEnvironment = "staging",
}: ConfigureDataElementsCoreProps) {
  // ================================================================
  // DEAL SOURCE (CR426: Sample ↔ Live)
  // ================================================================
  
  const [facesConfig, setFacesConfig] = useState<CardFacesConfig>(DEFAULT_FACES_CONFIG);
  const [isDragging, setIsDragging] = useState(false);
  
  const dealSource = useDealSourceMode(publishEnvironment, selectedType);
  const { deal: ssotDeal, meta: ssotMeta, loading: ssotLoading, error: ssotError } = dealSource;
  
  // ================================================================
  // PREVIEW OVERRIDES (Data Elements draft + Saved Components)
  // ================================================================
  
  const registryOverride = useMemo(() => {
    const original = registry.find(r => r.deal_type_key === selectedType);
    if (!original) return null;
    return {
      ribbon_text: savedRibbon.ribbon_text,
      ribbon_theme: savedRibbon.ribbon_theme,
      is_enabled: savedRibbon.is_enabled,
    };
  }, [registry, selectedType, savedRibbon]);
  
  const templateOverride = useMemo(() => ({
    sections: draftSections as unknown as Record<string, boolean>,
    labels: savedLabels as unknown as Record<string, string>,
    header: savedHeader as unknown as Record<string, unknown>,
    extended_sections: {
      ribbon: draftSections.ribbon,
      debug_strip: draftSections.debug_strip,
      merchant_category_chips: draftSections.merchant_category_chips,
      merchant_name: draftSections.merchant_name,
      confidence_indicator: draftSections.confidence_indicator,
      claim_button: draftSections.claim_button,
    },
    styling: {
      ...savedStyling,
      showRibbon: savedStyling.showRibbon,
      showStarburst: savedStyling.showStarburst,
      showMerchantLogo: savedStyling.showMerchantLogo,
      showMerchantName: savedStyling.showMerchantName,
    } as unknown as Record<string, unknown>,
    section_order: draftSectionOrder,
    theme_preset: savedThemePreset,
    surfaceOverrides: {
      primary: { blockToggles: draftSurfaceBlockToggles?.primary || {} },
      details: { blockToggles: draftSurfaceBlockToggles?.details || {} },
    },
    components: savedComponents ?? {},
  }), [
    draftSections, savedLabels, savedHeader, savedStyling, draftSectionOrder,
    savedThemePreset, draftSurfaceBlockToggles, savedComponents,
  ]);
  
  // ================================================================
  // DEAL TYPE OPTIONS
  // ================================================================
  
  const dealTypeOptions = useMemo(() => {
    const types = registry.map(r => r.deal_type_key);
    if (!types.includes(selectedType)) types.push(selectedType);
    return types.sort();
  }, [registry, selectedType]);
  
  // ================================================================
  // RENDER
  // ================================================================
  
  return (
    <div className="space-y-4">
      {/* TOP BAR */}
      <div className="flex items-center justify-between border-b pb-3">
        <div className="flex items-center gap-3">
          <Label className="text-sm font-medium text-muted-foreground">Deal Type:</Label>
          <Select value={selectedType} onValueChange={onTypeChange}>
            <SelectTrigger className="w-[180px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {dealTypeOptions.map((type) => (
                <SelectItem key={type} value={type} className="text-sm">
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* CR413: Publish target indicator */}
          <PublishTargetIndicator environment={publishEnvironment} />
        </div>
        
        <div className="flex items-center gap-2">
          {hasUnsavedChanges && (
            <Badge variant="secondary" className="text-warning bg-warning/10 border-warning/30">
              <AlertCircle className="h-3 w-3 mr-1" />
              Unsaved
            </Badge>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onDiscard}
            disabled={!hasUnsavedChanges || saving}
          >
            <Undo2 className="h-4 w-4 mr-1" />
            Discard
          </Button>
          <Button
            size="sm"
            onClick={onSave}
            disabled={!hasUnsavedChanges || saving}
          >
            <Save className="h-4 w-4 mr-1" />
            {saving ? "Saving..." : "Save Data Elements"}
          </Button>
        </div>
      </div>
      
      {/* 2-COLUMN LAYOUT */}
      <div className="grid grid-cols-12 gap-4">
        {/* LEFT: Editor Subtabs */}
        <div className="col-span-5">
          <Card>
            <CardContent className="p-4">
              <Tabs defaultValue="structure" className="space-y-4">
                <TabsList className="grid grid-cols-3 h-9">
                  <TabsTrigger value="structure" className="text-xs gap-1">
                    <LayoutGrid className="h-3 w-3" />
                    Structure
                  </TabsTrigger>
                  <TabsTrigger value="details" className="text-xs gap-1">
                    <FileText className="h-3 w-3" />
                    Details
                  </TabsTrigger>
                  <TabsTrigger value="promo" className="text-xs gap-1">
                    <Award className="h-3 w-3" />
                    Promo
                  </TabsTrigger>
                </TabsList>
                
                {/* STRUCTURE TAB */}
                <TabsContent value="structure" className="mt-4">
                  <ScrollArea className="h-[880px] pr-3">
                    <SectionChecklist
                      sections={draftSections}
                      onSectionToggle={onSectionToggle}
                      surfaceBlockToggles={draftSurfaceBlockToggles}
                      onSurfaceBlockToggle={onSurfaceBlockToggle}
                      surfaceBlockOrder={draftSurfaceBlockOrder}
                      onSurfaceBlockOrderChange={onSurfaceBlockOrderChange}
                      styleOverrides={savedStyling as unknown as Record<string, unknown>}
                      onStyleOverrideChange={(key, value) => {
                        if (key === 'showStarburst') {
                          onStylingChange({ showStarburst: value });
                        } else if (key === 'showRibbon') {
                          onStylingChange({ showRibbon: value });
                        }
                      }}
                      fieldMap={[]}
                      warnings={[]}
                      dealType={selectedType}
                      onDragStateChange={setIsDragging}
                    />
                  </ScrollArea>
                </TabsContent>
                
                {/* DETAILS CONTENT TAB — CR340: always expanded, no accordion */}
                <TabsContent value="details" className="mt-4">
                  <ScrollArea className="h-[600px] pr-3">
                    <DetailsContentAccordion
                      config={draftDetailsContent}
                      onChange={onDetailsContentChange}
                      alwaysExpanded
                    />
                  </ScrollArea>
                </TabsContent>
                
                {/* PROMO BADGES TAB — CR340: always expanded, no accordion */}
                <TabsContent value="promo" className="mt-4">
                  <ScrollArea className="h-[600px] pr-3">
                    <PromoBadgesAccordion
                      config={draftPromoBadges}
                      onChange={onPromoBadgesChange}
                      alwaysExpanded
                    />
                  </ScrollArea>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
        
        {/* RIGHT: Preview */}
        <div className="col-span-7 flex flex-col gap-4">
          {/* CR426: Deal Source Picker */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <DealSourcePicker
              mode={dealSource.mode}
              onModeChange={dealSource.setMode}
              meta={ssotMeta}
              loading={ssotLoading}
              liveDeals={dealSource.liveDeals}
              liveDealsLoading={dealSource.liveDealsLoading}
              liveSearch={dealSource.liveSearch}
              onLiveSearchChange={dealSource.setLiveSearch}
              selectedLiveDealId={dealSource.selectedLiveDealId}
              onSelectLiveDeal={dealSource.setSelectedLiveDealId}
            />
            {ssotError && (
              <Badge variant="destructive" className="text-[10px]">
                {ssotError}
              </Badge>
            )}
          </div>
          
          <div className="flex-1 min-h-[500px]">
            <PreviewWorkbench
              deal={ssotDeal || null}
              sampleDeal={null}
              dealType={selectedType}
              hasUnsavedChanges={hasUnsavedChanges}
              previewKey={`de-${publishEnvironment}-${selectedType}`}
              registryOverride={registryOverride}
              templateOverride={templateOverride}
              facesConfig={facesConfig}
              onFacesConfigChange={setFacesConfig}
              loading={isDragging}
              isAdmin={true}
              environment={publishEnvironment}
              renderMeta={ssotMeta?.renderPayload ?? null}
              className="h-full"
            />
          </div>
          
          {/* Saved Components info + debug */}
          <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/50 rounded border border-dashed space-y-1">
            <div>
              💡 Preview uses <span className="font-medium">saved component styling</span>. 
              Edit styling in the <span className="font-medium">Components</span> tab.
            </div>
            {savedComponents && (
              <div className="flex items-center gap-2 text-[10px] font-mono opacity-70">
                <span>src: components_json</span>
                {(savedComponents as Record<string, any>)?.ribbon?.custom_hex && (
                  <>
                    <span>·</span>
                    <span className="inline-flex items-center gap-1">
                      ribbon: 
                      <span 
                        className="inline-block w-3 h-3 rounded-sm border border-border" 
                        style={{ backgroundColor: (savedComponents as Record<string, any>).ribbon.custom_hex }} 
                      />
                      {(savedComponents as Record<string, any>).ribbon.custom_hex}
                    </span>
                  </>
                )}
                {!(savedComponents as Record<string, any>)?.ribbon?.custom_hex && (
                  <>
                    <span>·</span>
                    <span>ribbon: theme_default</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
